<template>
	<section class="business-section" :aria-label="t(`${I18N_NS}.business.ariaLabel`)">
		<header class="business-section__header">
			<div class="business-section__title-row">
				<img
					class="business-section__decor"
					src="../../../pc/images/title-decor-left.png"
					alt=""
					aria-hidden="true"
				/>
				<h2 class="business-section__title">{{ t(`${I18N_NS}.business.title`) }}</h2>
				<img
					class="business-section__decor"
					src="../../../pc/images/title-decor-right.png"
					alt=""
					aria-hidden="true"
				/>
			</div>
			<p class="business-section__subtitle">{{ t(`${I18N_NS}.business.subtitle`) }}</p>
		</header>

		<div class="business-section__list">
			<article
				v-for="(item, officeIndex) in list"
				:key="item.title"
				class="business-section__card"
				role="button"
				tabindex="0"
				@click="handleCardClick(item, officeIndex)"
				@keydown.enter="handleCardClick(item, officeIndex)"
			>
				<img class="business-section__image" :src="item.image" :alt="item.title" />
				<div class="business-section__body">
					<h3 class="business-section__card-title">{{ item.title }}</h3>
					<div class="business-section__contacts">
						<p
							v-for="(contact, index) in item.contacts"
							:key="`${item.title}-${contact.icon || contact.label}-${index}`"
							class="business-section__contact"
							@click.stop="handleContactClick(item, officeIndex, contact)"
						>
							<img
								v-if="contact.icon"
								class="business-section__icon"
								:src="iconMap[contact.icon]"
								alt=""
								aria-hidden="true"
							/>
							<span class="business-section__text">
								{{ contact.text || formatLegacyContact(contact) }}
							</span>
						</p>
					</div>
				</div>
			</article>
		</div>
	</section>
</template>

<script setup>
import iconLocation from "../../../pc/components/business-section/images/icon-location.png";
import iconId from "../../../pc/components/business-section/images/icon-id.png";
import iconPhone from "../../../pc/components/business-section/images/icon-phone.png";
import iconMobile from "../../../pc/components/business-section/images/icon-mobile.png";
import iconEmail from "../../../pc/components/business-section/images/icon-email.png";
import iconTime from "../../../pc/components/business-section/images/icon-time.png";
import iconWhatsapp from "../../../pc/components/business-section/images/icon-whatsapp.png";
import iconNumber from "../../../pc/components/business-section/images/icon-number.png";
import { useI18n } from "~/composables/useI18n";
import {
	BUSINESS_PARTNER_META,
	CONTACT_TYPE_MAP,
	useAboutUsTracking,
} from "../../../composables/useAboutUsTracking";

const I18N_NS = "aboutUs";
const { t } = useI18n();
const { track } = useAboutUsTracking("mobile");

defineProps({
	list: {
		type: Array,
		default: () => [],
	},
});

const iconMap = {
	location: iconLocation,
	id: iconId,
	phone: iconPhone,
	mobile: iconMobile,
	email: iconEmail,
	time: iconTime,
	whatsapp: iconWhatsapp,
	number: iconNumber,
};

const formatLegacyContact = contact => {
	if (contact.label) {
		return `${contact.label}：${contact.value || ""}`;
	}
	return contact.value || "\u00A0";
};

const getPartnerParams = (item, officeIndex, contactType) => {
	const meta = BUSINESS_PARTNER_META[officeIndex] || {};
	return {
		partner_id: meta.partner_id,
		partner_name: item.title,
		region: meta.region,
		contact_type: contactType,
	};
};

const handleCardClick = (item, officeIndex) => {
	track("business_partner_card_click", getPartnerParams(item, officeIndex));
};

const handleContactClick = (item, officeIndex, contact) => {
	const contactType = CONTACT_TYPE_MAP[contact.icon];
	if (!contactType) return;
	track("business_partner_card_click", getPartnerParams(item, officeIndex, contactType));
};
</script>

<style lang="scss" scoped>
.business-section {
	padding: 0 32px 56px;
	background: #ffffff;

	&__header {
		text-align: center;
	}

	&__title-row {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		margin-bottom: 4px;
	}

	&__decor {
		width: 94px;
		height: 12px;
		object-fit: contain;
		flex-shrink: 0;
	}

	&__title {
		margin: 0;
		color: #333333;
		font-size: 34px;
		font-weight: 700;
		line-height: 48px;
		white-space: nowrap;
	}

	&__subtitle {
		margin: 0 0 48px;
		color: #333333;
		font-size: 24px;
		line-height: 34px;
		text-align: center;
	}

	&__list {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	&__card {
		background: #ffffff;
		border: 1px solid #dedede;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 8px 24px rgba(36, 24, 12, 0.04);
	}

	&__image {
		display: block;
		width: 100%;
		height: 320px;
		object-fit: cover;
	}

	&__body {
		padding: 20px;
	}

	&__card-title {
		padding-left: 6px;
		color: #333333;
		font-size: 30px;
		font-weight: 700;
		line-height: 60px;
	}

	&__contacts {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	&__contact {
		display: flex;
		align-items: flex-start;
		gap: 6px;
		margin: 0;
		color: #606060;
		font-size: 28px;
		line-height: 46px;
	}

	&__icon {
		width: 32px;
		height: 32px;
		flex-shrink: 0;
		margin-top: 8px;
		object-fit: contain;
	}

	&__text {
		flex: 1;
		min-width: 0;
		overflow-wrap: anywhere;
		word-break: normal;
	}
}
</style>
